import { injectable } from 'inversify-props';
import BaseService from '@/services/base-service';
import { PaginationModel } from '@/models/pagination.model';
import EvoTalksConversaResumoModel from '@/models/crm/evotalks-conversa-resumo.model';
import EvoTalksMensagemModel from '@/models/crm/evotalks-mensagem.model';
import EvoTalksConversaFilterModel from '@/models/crm/evotalks-conversa-filter.model';
import EvoTalksFilaModel from '@/models/crm/evotalks-fila.model';
import EvoTalksAgenteModel from '@/models/crm/evotalks-agente.model';

export interface ICRMEvoTalksSyncEmpresaResult {
  synced: boolean;
  contatoId: number | null;
  evotalksClientId: string | null;
}

export interface ICRMEvoTalksSyncContatosResult {
  total: number;
  sincronizados: number;
  ignorados: number;
  erros: number;
}

export interface ICRMEvoTalksStatusResult {
  empresaSincronizada: boolean;
  evotalksClientId: string | null;
  totalContatos: number;
  contatosSincronizados: number;
  ultimaSincronizacaoEmpresa: string | null;
  ultimaSincronizacaoContatos: string | null;
}

export interface ICRMEvoTalksSyncClienteEmpresaResult {
  empresaSincronizada: boolean;
  evoCompanyId: string | null;
  contatoSincronizado: boolean;
  evoClientId: string | null;
}

export interface ICRMEvoTalksStatusClienteResult {
  clienteSincronizado: boolean;
  evoCompanyId: string | null;
  contatoPrincipalSincronizado: boolean;
  evoClientId: string | null;
  totalContatos: number;
  contatosSincronizados: number;
  ultimaSincronizacaoEmpresa: string | null;
  ultimaSincronizacaoContatos: string | null;
}

@injectable()
export default class CRMEvoTalksService extends BaseService {
  async syncEmpresa(cnpj: string | null, prospectId: number | null): Promise<ICRMEvoTalksSyncEmpresaResult> {
    const params = this.buildParams(cnpj, prospectId);
    const response = await this.httpService.post(
      Object,
      `${this.getApiPath()}/crm/evotalks/contatos/sync-empresa?${params}`,
    );
    return response as unknown as ICRMEvoTalksSyncEmpresaResult;
  }

  async syncContatos(cnpj: string | null, prospectId: number | null): Promise<ICRMEvoTalksSyncContatosResult> {
    const params = this.buildParams(cnpj, prospectId);
    const response = await this.httpService.post(
      Object,
      `${this.getApiPath()}/crm/evotalks/contatos/sync-contatos?${params}`,
    );
    return response as unknown as ICRMEvoTalksSyncContatosResult;
  }

  async getStatus(cnpj: string | null, prospectId: number | null): Promise<ICRMEvoTalksStatusResult> {
    const params = this.buildParams(cnpj, prospectId);
    const response = await this.httpService.get(
      Object,
      `${this.getApiPath()}/crm/evotalks/contatos/status?${params}`,
    );
    return response as unknown as ICRMEvoTalksStatusResult;
  }

  async syncClienteEmpresa(
    cnpj: string | null,
    clienteId: number | null,
  ): Promise<ICRMEvoTalksSyncClienteEmpresaResult> {
    const params = this.buildClienteParams(cnpj, clienteId);
    const response = await this.httpService.post(
      Object,
      `${this.getApiPath()}/crm/evotalks/clientes/contatos/sync-empresa?${params}`,
    );
    return response as unknown as ICRMEvoTalksSyncClienteEmpresaResult;
  }

  async syncClienteContatos(cnpj: string | null, clienteId: number | null): Promise<ICRMEvoTalksSyncContatosResult> {
    const params = this.buildClienteParams(cnpj, clienteId);
    const response = await this.httpService.post(
      Object,
      `${this.getApiPath()}/crm/evotalks/clientes/contatos/sync-contatos?${params}`,
    );
    return response as unknown as ICRMEvoTalksSyncContatosResult;
  }

  async getClienteStatus(cnpj: string | null, clienteId: number | null): Promise<ICRMEvoTalksStatusClienteResult> {
    const params = this.buildClienteParams(cnpj, clienteId);
    const response = await this.httpService.get(
      Object,
      `${this.getApiPath()}/crm/evotalks/clientes/contatos/status?${params}`,
    );
    return response as unknown as ICRMEvoTalksStatusClienteResult;
  }

  async queryConversations(
    page: number,
    limit: number,
    filter: EvoTalksConversaFilterModel,
  ): Promise<PaginationModel<EvoTalksConversaResumoModel>> {
    const response = await this.httpService.post(
      Object,
      `${this.getApiPath()}/crm/evotalks/conversa/query?page=${page}&limit=${limit}`,
      filter,
    );
    return response as unknown as PaginationModel<EvoTalksConversaResumoModel>;
  }

  async getResumoById(id: number): Promise<EvoTalksConversaResumoModel> {
    return (await this.httpService.get(
      EvoTalksConversaResumoModel,
      `${this.getApiPath()}/crm/evotalks/conversa/resumo/${id}`,
    )) as EvoTalksConversaResumoModel;
  }

  async getChatMessages(chatId: string): Promise<EvoTalksMensagemModel[]> {
    const response = await this.httpService.get(
      Object,
      `${this.getApiPath()}/crm/evotalks/conversa/${encodeURIComponent(chatId)}/messages`,
    );
    return response as unknown as EvoTalksMensagemModel[];
  }

  async getChatMessagesFromApi(chatId: string): Promise<EvoTalksMensagemModel[]> {
    const response = await this.httpService.get(
      Object,
      `${this.getApiPath()}/crm/evotalks/conversa/${encodeURIComponent(chatId)}/messages-api`,
    );
    return response as unknown as EvoTalksMensagemModel[];
  }

  getFileDownloadUrl(chatId: string, fileId: string): string {
    const path = `${this.getApiPath()}/crm/evotalks/conversa/${encodeURIComponent(chatId)}`;
    return `${path}/file/${encodeURIComponent(fileId)}`;
  }

  async getContatoChats(chatId: string): Promise<EvoTalksConversaResumoModel[]> {
    const response = await this.httpService.get(
      Object,
      `${this.getApiPath()}/crm/evotalks/conversa/${encodeURIComponent(chatId)}/contato-chats`,
    );
    return response as unknown as EvoTalksConversaResumoModel[];
  }

  async getFilas(): Promise<EvoTalksFilaModel[]> {
    const response = await this.httpService.get(
      Object,
      `${this.getApiPath()}/crm/evotalks/filas`,
    );
    return response as unknown as EvoTalksFilaModel[];
  }

  async getAgentes(): Promise<EvoTalksAgenteModel[]> {
    const response = await this.httpService.get(
      Object,
      `${this.getApiPath()}/crm/evotalks/conversa/agentes`,
    );
    return response as unknown as EvoTalksAgenteModel[];
  }

  // eslint-disable-next-line class-methods-use-this
  private buildParams(cnpj: string | null, prospectId: number | null): string {
    const parts: string[] = [];
    if (cnpj) parts.push(`cnpj=${encodeURIComponent(cnpj)}`);
    if (prospectId) parts.push(`prospectId=${prospectId}`);
    return parts.join('&');
  }

  // eslint-disable-next-line class-methods-use-this
  private buildClienteParams(cnpj: string | null, clienteId: number | null): string {
    const parts: string[] = [];
    if (cnpj) parts.push(`cnpj=${encodeURIComponent(cnpj)}`);
    if (clienteId) parts.push(`clienteId=${clienteId}`);
    return parts.join('&');
  }
}
